import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosGateway } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";

const dbg = (...a) => {
  if (typeof window === "undefined" || window.__POSTS_DEBUG__ !== true) return;
  console.debug("[postsThunks]", ...a);
};

const getFetchPostsError = (error) => {
  const responseMessage =
    error?.response?.data?.message || error?.response?.data?.error;
  if (Array.isArray(responseMessage)) return responseMessage.join(", ");
  if (responseMessage) return String(responseMessage);

  if (!error?.response) {
    return `Unable to reach the posts API: ${error?.message || "network request failed"}`;
  }
  return `Posts API request failed (HTTP ${error.response.status})`;
};

/* ======================== UPLOAD FILES ======================== */
export const uploadFiles = createAsyncThunk(
  "posts/uploadFiles",
  async (files, { rejectWithValue }) => {
    try {
      if (!files || files.length === 0) throw new Error("No files provided for upload");

      const validFiles = files.filter((file) => file instanceof File);
      if (validFiles.length === 0) throw new Error("No valid files found");

      const formData = new FormData();
      validFiles.forEach((file) => formData.append("files", file));

      const url = ENDPOINTS.POSTS?.UPLOAD || "/posts/upload";
      dbg("➡️ uploadFiles", { url, count: validFiles.length });

      const res = await axiosGateway.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });

      dbg("✅ uploadFiles success", res.data);
      return res.data?.data ?? [];
    } catch (err) {
      console.error("[postsThunks] uploadFiles error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to upload files"
      );
    }
  }
);

/* ======================== GET ALL POSTS (FEED) ======================== */
export const fetchPosts = createAsyncThunk(
  "posts/fetchAll",
  async ({ page = 1, limit, pageSize } = {}, { rejectWithValue }) => {
    try {
      const effectiveLimit =
        typeof limit === "number"
          ? limit
          : typeof pageSize === "number"
          ? pageSize
          : undefined;

      const url = ENDPOINTS.POSTS?.ALL || "/posts";
      const params = effectiveLimit ? { page, limit: effectiveLimit } : { page };

      dbg("➡️ fetchPosts", { url, params });

      const res = await axiosGateway.get(url, { params });
      dbg("✅ fetchPosts success", res.data);

      return res.data;
    } catch (err) {
      console.error("[postsThunks] fetchPosts error:", err);
      return rejectWithValue(getFetchPostsError(err));
    }
  },
  {
    condition: (_, { getState }) =>
      getState().posts?.feed?.status !== "loading",
  }
);

/* ===================== GET POSTS BY USER ======================= */
export const fetchPostsByUser = createAsyncThunk(
  "posts/fetchPostsByUser",
  async ({ userId, page = 1, limit, pageSize } = {}, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error("userId is required to fetch posts by user");

      const effectiveLimit =
        typeof limit === "number"
          ? limit
          : typeof pageSize === "number"
          ? pageSize
          : undefined;

      const url = ENDPOINTS.POSTS?.BY_USER
        ? ENDPOINTS.POSTS.BY_USER(userId)
        : `/posts/user/${encodeURIComponent(String(userId))}`;

      const params = effectiveLimit ? { page, limit: effectiveLimit } : { page };

      dbg("➡️ fetchPostsByUser", { url, params });

      const res = await axiosGateway.get(url, { params });

      return {
        items: res.data?.data ?? [],
        total: res.data?.total ?? 0,
        userId,
      };
    } catch (err) {
      console.error("[postsThunks] fetchPostsByUser error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to fetch posts by user"
      );
    }
  }
);

/* ======================== CREATE POST ========================== */
/**
 * dto attendu: { userId, payload }
 * ✅ Gateway: POST /posts/create/:userId
 */
export const createPost = createAsyncThunk(
  "posts/createPost",
  async ({ userId, payload }, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error("userId is required to create a post");
      if (!payload?.content) throw new Error("content is required");

      const url = ENDPOINTS.POSTS?.CREATE
        ? ENDPOINTS.POSTS.CREATE(userId)
        : `/posts/create/${encodeURIComponent(String(userId))}`;

      dbg("➡️ createPost", { url, payload });

      const res = await axiosGateway.post(url, payload);
      return res.data?.data;
    } catch (err) {
      console.error("[postsThunks] createPost error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to create post"
      );
    }
  }
);

/* ======================== UPDATE POST ========================== */
/**
 * ✅ Gateway: PUT /posts/update/by-post-id/:postId/:userId
 * payload: { postId, userId, data }
 */
export const updatePost = createAsyncThunk(
  "posts/updatePost",
  async ({ postId, userId, data }, { rejectWithValue }) => {
    try {
      if (!postId) throw new Error("postId is required to update post");
      if (!userId) throw new Error("userId is required to update post");

      const url = ENDPOINTS.POSTS?.UPDATE_BY_POST_ID
        ? ENDPOINTS.POSTS.UPDATE_BY_POST_ID(postId, userId)
        : `/posts/update/by-post-id/${encodeURIComponent(String(postId))}/${encodeURIComponent(String(userId))}`;

      dbg("➡️ updatePost", { url, data });

      const res = await axiosGateway.put(url, data);
      return res.data?.data;
    } catch (err) {
      console.error("[postsThunks] updatePost error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to update post"
      );
    }
  }
);

/* ======================== DELETE POST ========================== */
/**
 * ✅ Gateway: DELETE /posts/delete/by-post-id/:postId
 */
export const deletePost = createAsyncThunk(
  "posts/deletePost",
  async ({ postId, userId }, { rejectWithValue }) => {
    try {
      if (!postId) throw new Error("postId is required to delete post");
      if (!userId) throw new Error("userId is required to delete post");

      const url = ENDPOINTS.POSTS?.DELETE_BY_POST_ID
        ? ENDPOINTS.POSTS.DELETE_BY_POST_ID(postId, userId)
        : `/posts/delete/by-post-id/${encodeURIComponent(String(postId))}/${encodeURIComponent(String(userId))}`;

      dbg("➡️ deletePost", { url });

      const res = await axiosGateway.delete(url);

      return {
        postId,
        message: res.data?.message || "Post deleted",
      };
    } catch (err) {
      console.error("[postsThunks] deletePost error:", err);
      return rejectWithValue(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to delete post"
      );
    }
  }
);